import React, { useState, useEffect, useCallback } from 'react';
import { Table, Badge, Button, Spinner, Alert, Modal, Form } from 'react-bootstrap';
import { FaFilePdf, FaBan, FaEye } from 'react-icons/fa';
import moment from 'moment';
import { getHojasTrabajoPorOt, getHojaTrabajoDetalle, anularHojaTrabajo } from '../../api/common';
import { PdfHojaTrabajo } from './PdfHojaTrabajo';
import { useAuth } from '../../hooks/useAuth';
import { Alertassw } from '../Alertas/Alertassw';

const ROLES_PUEDEN_ANULAR = ['Admin', 'Directora de laboratorio', 'Coordinadora de calidad'];

const HojasTrabajoList = ({ otId, refreshToken = 0 }) => {
  const { userData } = useAuth();
  const rolNombre = userData?.role?.nombre || userData?._doc?.role?.nombre;
  const puedeAnular = ROLES_PUEDEN_ANULAR.includes(rolNombre);

  const [hojas, setHojas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalAnular, setModalAnular] = useState({ show: false, hoja: null });
  const [motivo, setMotivo] = useState('');
  const [anulando, setAnulando] = useState(false);

  const cargar = useCallback(async () => {
    if (!otId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getHojasTrabajoPorOt(otId);
      setHojas(res?.data?.data || res?.data || []);
    } catch (err) {
      console.error('Error listar hojas de trabajo:', err);
      setError(err?.response?.data?.message || 'No se pudieron cargar las hojas de trabajo.');
    } finally {
      setLoading(false);
    }
  }, [otId]);

  useEffect(() => {
    cargar();
  }, [cargar, refreshToken]);

  const handleVerPdf = async (hoja) => {
    try {
      const res = await getHojaTrabajoDetalle(hoja._id);
      const detalle = res?.data?.data || res?.data;
      PdfHojaTrabajo(detalle);
    } catch (err) {
      console.error('Error cargar detalle hoja:', err);
      Alertassw('error', 'Error', 'No se pudo generar el PDF de la hoja.');
    }
  };

  const handleAnular = async () => {
    if (!motivo || motivo.trim().length < 5) {
      Alertassw('warning', 'Motivo requerido', 'Ingresa un motivo de al menos 5 caracteres.');
      return;
    }
    setAnulando(true);
    try {
      await anularHojaTrabajo(modalAnular.hoja._id, motivo.trim());
      Alertassw('success', 'Hoja anulada', 'La hoja fue anulada correctamente.');
      setModalAnular({ show: false, hoja: null });
      setMotivo('');
      cargar();
    } catch (err) {
      console.error('Error anular hoja:', err);
      Alertassw('error', 'Error', err?.response?.data?.message || 'No se pudo anular la hoja.');
    } finally {
      setAnulando(false);
    }
  };

  if (loading) {
    return <div className="text-center p-3"><Spinner animation="border" size="sm" /> Cargando hojas...</div>;
  }

  if (error) {
    return <Alert variant="danger" className="my-2">{error}</Alert>;
  }

  if (!hojas.length) {
    return <div className="text-muted small p-2">Sin hojas de trabajo emitidas para esta OT.</div>;
  }

  return (
    <>
      <Table size="sm" hover responsive className="mb-0">
        <thead>
          <tr>
            <th>#</th>
            <th>Fecha</th>
            <th>Autor</th>
            <th className="text-center">Equipos</th>
            <th className="text-center">Estado</th>
            <th className="text-end">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {hojas.map((h) => (
            <tr key={h._id}>
              <td><strong>{h.consecutivo}</strong></td>
              <td>{moment(h.fechaGeneracion).format('YYYY-MM-DD HH:mm')}</td>
              <td>{h.autor?.nombre} {h.autor?.apellido}</td>
              <td className="text-center">{h.equipos?.length ?? 0}</td>
              <td className="text-center">
                <Badge bg={h.estado === 'Anulada' ? 'danger' : 'success'}>
                  {h.estado || 'Emitida'}
                </Badge>
              </td>
              <td className="text-end">
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={() => handleVerPdf(h)}
                  className="me-1"
                  title="Ver PDF"
                >
                  <FaFilePdf />
                </Button>
                {puedeAnular && h.estado !== 'Anulada' && (
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => setModalAnular({ show: true, hoja: h })}
                    title="Anular hoja"
                  >
                    <FaBan />
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Modal show={modalAnular.show} onHide={() => !anulando && setModalAnular({ show: false, hoja: null })} centered>
        <Modal.Header closeButton>
          <Modal.Title>Anular Hoja de Trabajo #{modalAnular.hoja?.consecutivo}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning" className="small">
            La anulación NO revierte el estado de equipos ni el avance de la OT.
            Queda registrada en el audit trail (ISO 17025 §6.2.5).
          </Alert>
          <Form.Group>
            <Form.Label>Motivo de anulación *</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              maxLength={500}
              disabled={anulando}
              placeholder="Ej: error en snapshot de mediciones, hoja duplicada, etc."
            />
            <Form.Text className="text-muted">{motivo.length}/500 caracteres.</Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setModalAnular({ show: false, hoja: null })} disabled={anulando}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleAnular} disabled={anulando}>
            {anulando ? <><Spinner animation="border" size="sm" className="me-2" />Anulando...</> : <><FaBan className="me-1" /> Confirmar Anulación</>}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default HojasTrabajoList;
